// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { geocodeAddress } from '@hopdrive/sdk-lanes';
import { useAddressSuggestions } from '@hooks/useAddressSuggestions';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import type { PickedLocation } from '../utils/guestRideUtils';

import { Autocomplete, TextField, InputAdornment, MenuItem, Typography, CircularProgress, useTheme } from '@mui/material';
import { BiLogoGoogle, BiSolidMapPin } from 'react-icons/bi';

// TYPES ---------------------------------------------------------------- //

interface GuestRideAddressInputProps {
  label?: string;
  placeholder?: string;
  value: PickedLocation | null;
  onChange: (loc: PickedLocation | null) => void;
  error?: string;
  disabled?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const GuestRideAddressInput: React.FC<GuestRideAddressInputProps> = ({
  label,
  placeholder,
  value,
  onChange,
  error,
  disabled,
}) => {
  const theme = useTheme();
  const [inputText, setInputText] = React.useState(value?.address ?? '');
  const [resolving, setResolving] = React.useState(false);

  const { suggestions, loading: loadingSuggestions, search, clear } = useAddressSuggestions();

  // Sync inputText when value is set externally (e.g. pre-populate in EditRideModal)
  React.useEffect(() => {
    setInputText(value?.address ?? '');
  }, [value?.address]);

  const handleInputChange = (newValue: string, reason: string) => {
    setInputText(newValue);
    if (reason === 'input') {
      search(newValue);
    } else {
      clear();
    }
  };

  const handleSelect = async (suggestion: { mainText: string; secondaryText: string; placeId: string; fullText: string } | null) => {
    if (!suggestion) {
      onChange(null);
      setInputText('');
      return;
    }

    setResolving(true);
    try {
      const result = await geocodeAddress(suggestion.fullText, undefined, suggestion.placeId);
      onChange({
        address: result.formattedAddress,
        lat: result.latitude,
        lng: result.longitude,
      });
      setInputText(result.formattedAddress);
    } catch (err) {
      console.error('Geocoding failed:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: 'geocode-address' },
        extra: { query: suggestion.fullText, placeId: suggestion.placeId },
      });
      toast.error('Could not resolve address, please try another.');
      onChange(null);
    } finally {
      setResolving(false);
    }
  };

  const isLoading = loadingSuggestions || resolving;

  return (
    <Autocomplete
      fullWidth
      freeSolo
      loading={isLoading}
      loadingText='Searching...'
      noOptionsText='No results found'
      options={suggestions}
      getOptionLabel={option => (typeof option === 'string' ? option : option.fullText)}
      getOptionKey={option => (typeof option === 'string' ? option : option.placeId)}
      isOptionEqualToValue={(option, val) =>
        typeof option !== 'string' && typeof val !== 'string' && option.placeId === val.placeId
      }
      value={null}
      inputValue={inputText}
      disabled={disabled || resolving}
      onChange={(_event, selected) => {
        if (selected && typeof selected !== 'string') {
          handleSelect(selected);
        } else if (!selected) {
          handleSelect(null);
        }
      }}
      onInputChange={(_event, newVal, reason) => handleInputChange(newVal, reason)}
      renderOption={(props, option) => {
        if (typeof option === 'string') return null;
        return (
          <MenuItem {...props} key={option.placeId}>
            <BiLogoGoogle size={18} style={{ color: 'grey', flexShrink: 0, marginRight: 10 }} />
            <span>
              <Typography variant='body2' sx={{ fontWeight: 500, display: 'block' }}>
                {option.mainText}
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                {option.secondaryText}
              </Typography>
            </span>
          </MenuItem>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error}
          required={!!label}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start' sx={{ marginLeft: `8px`, marginRight: 0 }}>
                  {isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <BiSolidMapPin size={18} />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRideAddressInput;
